'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { NewsletterForm } from '../blocks/NewsletterForm'
import { BellRing, CircleCheck, PackageSearch } from '@/components/homepage-october/lucide'

import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/homepage-october/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { ToggleGroup, ToggleGroupItem } from '@/components/homepage-october/ui/toggle-group'
import { Section, SectionHeader } from '../blocks/layout'
import { ProductRail } from '../blocks/Rail'
import { RetailerMark } from '@/components/homepage-2026/retailer-mark'
import type { Model } from '../types'

// Tab label → the card kind it keeps (v2's own mapping).
const KIND: Record<string, string | null> = { All: null, Fridges: 'Fridge', Freezers: 'Freezer', 'Mini fridges': 'Mini fridge', 'Wine & beverage': 'Wine & beverage' }

export function AlertForm({ m }: { m: Model['deals']['alert']; id: string }) { return <NewsletterForm source="homepage-deals" button={m.button} /> }

export function Deals({ m }: { m: Model['deals'] }) {
  const [tab, setTab] = React.useState('All')
  const kind = KIND[tab] ?? null
  return (
    <Section aria={m.aria} v2="V04" tone="peach">
      <SectionHeader head={m.head} tone="peach" />
      <ToggleGroup
        type="single"
        value={tab}
        onValueChange={(v) => v && setTab(v)}
        aria-label={m.tabsAria}
        className="mb-8 flex-wrap justify-start gap-2"
      >
        {m.tabs.map((t) => (
          <ToggleGroupItem
            key={t}
            value={t}
            className="h-9! flex-none! rounded-full! border border-neutral-200 bg-white px-4! text-sm font-medium text-neutral-700 shadow-xs hover:border-neutral-300 data-[state=on]:border-[#0469f0] data-[state=on]:bg-[#0469f0]! data-[state=on]:text-white!"
          >
            {t}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <div className={compactClassName("grid grid-cols-1 gap-8")}>
        {m.rows.map((r, k) => {
          const list = kind ? r.products.filter((p) => p.kind === kind) : r.products
          if (!list.length && m.rows.some(row => row.products.some(p => !kind || p.kind === kind))) return null
          const title = r.label
          return (
            <div key={r.label}>
              {list.length ? (
                <ProductRail
                  products={list}
                  title={title}
                  autoplay={k === 0 ? 4200 : 5200}
                  eagerFirst={k === 0 ? 4 : 0}
                  kicker={
                    <div className={compactClassName("mb-2 flex flex-wrap items-center gap-2")}>
                      {r.pill ? <Badge className="border-transparent bg-[#067647]! text-white!">{r.pill}</Badge> : null}
                      <span className={compactClassName("text-xs font-medium text-neutral-600")}>{list.length} {list.length === 1 ? 'model' : 'models'}</span>
                    </div>
                  }
                />
              ) : (
                <h3 className={compactClassName("mb-4 text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>{title}</h3>
              )}
              {/* v2 keeps this message in the page and shows it only when a filter empties the row; so does v3. */}
              <Empty hidden={list.length > 0} className="border border-dashed bg-white">
                <EmptyHeader>
                  <EmptyMedia variant="icon"><PackageSearch aria-hidden="true" /></EmptyMedia>
                  <EmptyTitle>{m.empty.title.replace('{kind}', kind ? tab.toLowerCase() : 'models')}</EmptyTitle>
                  <EmptyDescription>
                    {m.empty.text} <DestinationLink href={m.empty.link.href} className="font-medium text-[#0454c0] underline underline-offset-2">{m.empty.link.text}</DestinationLink>.{' '}
                    <button type="button" className={compactClassName("font-medium text-[#0454c0] underline underline-offset-2")} onClick={() => setTab('All')}>Show all {r.products.length} models</button>
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          )
        })}
      </div>
      <Card id={m.alert.id} className="mt-8 gap-4! overflow-hidden border-[#ff9951]/30 bg-white px-5! py-5! md:flex-row! md:items-center md:gap-8! md:px-8!">
        <div className={compactClassName("flex items-start gap-4 md:max-w-md")}>
          <span className={compactClassName("flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#fff1e6] text-[#b54708]")}><BellRing aria-hidden="true" data-motion="" className="v3-bell size-5" /></span>
          <div>
            <h3 className={compactClassName("text-lg font-semibold text-neutral-950")}>{m.alert.title}</h3>
            <p className={compactClassName("mt-1 text-sm text-neutral-600")}>{m.alert.text}</p>
          </div>
        </div>
        <div className={compactClassName("flex-1")}><AlertForm m={m.alert} id="v3-alert-email" /></div>
      </Card>
      <div className={compactClassName("mt-8 flex flex-wrap items-center gap-x-4 gap-y-3 text-sm")}>
        <span className={compactClassName("font-medium text-neutral-700")}>{m.stores.label}</span>
        <ul className={compactClassName("flex flex-wrap items-center gap-2")}>
          {m.stores.items.map((s) => (
            <li key={s.name} className={compactClassName("flex h-9 items-center gap-2 rounded-full border bg-white px-3 font-medium text-neutral-800 shadow-xs")}>
              <RetailerMark name={s.name} />
              {s.name}
            </li>
          ))}
        </ul>
        {m.stores.more ? <Badge variant="outline" className="h-9 rounded-full! bg-white px-3 text-sm!">{m.stores.more}</Badge> : null}
      </div>
    </Section>
  )
}
